import { useCallback, useEffect, useRef, useState } from "react";
import { useBackend } from "../../lib/backend";
import {
  readRiskFlowAlertCache,
  writeRiskFlowAlertCache,
} from "../../lib/riskflow-alert-cache";
import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import { backendItemToRiskFlowAlert } from "./riskflow-backend-adapter";

const BACKEND_FEED_POLL_MS = 15_000;

export function useRiskFlowFeed(activeInstrument: string) {
  const backend = useBackend();
  const [alerts, setAlerts] = useState<RiskFlowAlert[]>(() =>
    readRiskFlowAlertCache(activeInstrument),
  );
  const [alertScope, setAlertScope] = useState(activeInstrument);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchStatus, setFetchStatus] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(alerts.length > 0);
  const loadedCountRef = useRef(50);
  const activePollRef = useRef<{ instrument: string; token: number } | null>(
    null,
  );
  const pollTokenRef = useRef(0);
  const outageActiveRef = useRef(false);
  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setTemporaryStatus = useCallback(
    (message: string, duration: number) => {
      setFetchStatus(message);
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
      statusTimerRef.current = setTimeout(() => setFetchStatus(""), duration);
    },
    [],
  );

  useEffect(() => {
    pollTokenRef.current += 1;
    activePollRef.current = null;
    const cached = readRiskFlowAlertCache(activeInstrument);
    setAlertScope(activeInstrument);
    setAlerts(cached);
    setInitialLoaded(cached.length > 0);
    loadedCountRef.current = Math.max(50, cached.length);
  }, [activeInstrument]);

  const poll = useCallback(async () => {
    if (activePollRef.current?.instrument === activeInstrument) return;
    const token = ++pollTokenRef.current;
    activePollRef.current = { instrument: activeInstrument, token };
    try {
      setFetchStatus("Fetching scored items...");
      const response = await backend.riskflow.list({
        minMacroLevel: 0,
        limit: loadedCountRef.current,
        instrument: activeInstrument,
      });
      const nextAlerts = response.items.map((item) =>
        backendItemToRiskFlowAlert(item, activeInstrument),
      );
      if (token !== pollTokenRef.current) return;
      setAlerts(nextAlerts);
      writeRiskFlowAlertCache(activeInstrument, nextAlerts);
      setHasMore(response.hasMore ?? false);
      setInitialLoaded(true);
      if (outageActiveRef.current) {
        outageActiveRef.current = false;
        setTemporaryStatus("Feed recovered", 3000);
      } else {
        setTemporaryStatus(`${nextAlerts.length} items loaded`, 2000);
      }
    } catch (error) {
      if (token !== pollTokenRef.current) return;
      setInitialLoaded(true);
      setFetchStatus("Feed unavailable");
      if (!outageActiveRef.current) {
        outageActiveRef.current = true;
        console.warn("[RiskFlowContext] Backend feed unavailable", error);
      }
    } finally {
      if (activePollRef.current?.token === token) {
        activePollRef.current = null;
      }
    }
  }, [activeInstrument, backend.riskflow, setTemporaryStatus]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const token = pollTokenRef.current;
    setLoadingMore(true);
    try {
      const response = await backend.riskflow.list({
        minMacroLevel: 0,
        limit: 50,
        offset: alerts.length,
        instrument: activeInstrument,
      });
      const newcomers = response.items.map((item) =>
        backendItemToRiskFlowAlert(item, activeInstrument),
      );
      if (token !== pollTokenRef.current) return;
      setAlerts((current) => {
        const next = [...current, ...newcomers];
        writeRiskFlowAlertCache(activeInstrument, next);
        return next;
      });
      setHasMore(response.hasMore ?? false);
      loadedCountRef.current = alerts.length + newcomers.length;
    } finally {
      setLoadingMore(false);
    }
  }, [activeInstrument, alerts.length, backend.riskflow, hasMore, loadingMore]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await poll();
    } finally {
      setRefreshing(false);
    }
  }, [poll]);

  useEffect(() => {
    void poll();
    const interval = setInterval(() => void poll(), BACKEND_FEED_POLL_MS);
    return () => clearInterval(interval);
  }, [poll]);

  useEffect(() => {
    const refreshVisibleFeed = () => {
      if (document.visibilityState === "visible") void poll();
    };
    document.addEventListener("visibilitychange", refreshVisibleFeed);
    return () =>
      document.removeEventListener("visibilitychange", refreshVisibleFeed);
  }, [poll]);

  useEffect(
    () => () => {
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    },
    [],
  );

  return {
    alerts,
    alertScope,
    fetchStatus,
    hasMore,
    initialLoaded,
    loadingMore,
    loadMore,
    refresh,
    refreshing,
  };
}
